import type { FunctionComponent } from "react";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Controller, useFieldArray } from "react-hook-form";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import type {
  IAddress,
  OptionType,
  PointOfContact,
  User,
} from "../../../../../types/types";
import {
  Flex,
  Flex1,
  Flex2,
  Form,
  SubmitButtonContainer,
} from "../../../../../layout/FormLayout";
import { TextField } from "../../../../../components/TextFields/TextFields";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  PrimaryButtonMedium,
  PrimaryButtonMediumV2,
  TertiaryButtonLargeV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import {
  getAddressOneInputLabel,
  getAddressTwoInputLabel,
  getStatesInputLabel,
  getZipCodeInputLabel,
  getCountryOption,
  getStateOption,
} from "../../../../../util/location";
import { useStoreState, useFormWrapper } from "../../../../../util/util";
import {
  zodAddressDefault,
  zodRequiredString,
  pointsOfContactArraySchema,
} from "../../../../../util/zod.util";
import {
  getCountryCode,
  getPhoneNumber,
  getPhoneCodeOption,
} from "../../../../../util/phone";
import { useCountriesList, useStatesList } from "../../../../../util/Locations";
import { useTranslation } from "react-i18next";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import styled from "styled-components";
import { Modal } from "../../../../../components/Modal/Modal";
import type { IAddressDetails } from "./EditLocations";
import { useAuthContext } from "../../../../../components/Auth";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import { PointOfContactAccordion } from "./PointOfContactAccordion";
import { existingUserPointOfContactOption } from "./PointOfContactForm";
import type { TwoLineOption } from "../../../../../components/SelectBoxV2/TwoLineOptionLabel";
import {
  FormSectionHeading,
  FullWidthButtonGroup,
} from "../../../../../components/Form/Form";

const ModalWrapper = styled.div`
  padding: 50px 90px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.small};
`;

const WarningTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.large};
  color: ${({ theme }) => theme.warningTextColor};
  margin-bottom: 30px;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_2xl")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

export interface LocationFormPOCInput {
  id?: string;
  poc_type: "manual" | "existing";
  contact_first_name?: string;
  contact_last_name?: string;
  email_address?: string;
  phone_number?: string;
  country_code?: OptionType;
  point_of_contact_id?: TwoLineOption;
}

export interface LocationInputs {
  company_name: string;
  external_id: string;
  tax_id?: string;
  crm_id?: string;
  address1: string;
  address2: string;
  country: OptionType;
  county: string;
  state: OptionType;
  city: string;
  postal_code: string;
  is_active: boolean;
  same_as_hq?: boolean;
  points_of_contact: LocationFormPOCInput[];
}

interface LocationFormProps {
  onSubmitFn: (inputs: LocationInputs) => Promise<boolean>;
  submitButtonText: string;
  rendersInModal?: boolean;
  address?: IAddress;
  editTaxCRM?: boolean;
  tenantId?: string;
  addressDetails?: IAddressDetails;
  hqAddress?: IAddress;
  onCancel?: () => void;
}

const blankPointOfContact: LocationFormPOCInput = { poc_type: "manual" };

const pointOfContactToFormInput = (poc: PointOfContact): LocationFormPOCInput =>
  poc.point_of_contact_user_id
    ? {
        id: poc.id,
        poc_type: "existing",
        point_of_contact_id: existingUserPointOfContactOption({
          id: poc.point_of_contact_user_id,
          name: poc.contact_name,
          label: `${poc.contact_name} - ${poc.email_address}`,
          email_address: poc.email_address,
          business_unit: poc.business_unit,
          country: poc.country,
        }),
      }
    : {
        id: poc.id,
        poc_type: "manual",
        contact_first_name: poc.contact_first_name,
        contact_last_name: poc.contact_last_name,
        email_address: poc.email_address,
        phone_number: getPhoneNumber(poc.phone_number),
        country_code: getPhoneCodeOption(getCountryCode(poc.phone_number)) as
          | OptionType
          | undefined,
      };

const addressToPointsOfContactInputs = (
  address?: IAddress
): LocationFormPOCInput[] =>
  address?.points_of_contact?.length
    ? address.points_of_contact.map(pointOfContactToFormInput)
    : [blankPointOfContact];

const normalizeOptionalField = (value?: string | null) => value?.trim() ?? "";

const didFieldChange = (
  nextValue?: string | null,
  previousValue?: string | null
) =>
  normalizeOptionalField(nextValue) !== normalizeOptionalField(previousValue);

/**
 * Used to create or edit a location. A location includes an address and one
 * or more points of contact. When an optional location/address is passed in,
 * populate the form with that data to edit that address, otherwise the form
 * is blank to create a new location. A passed in function is called when the
 * form is submitted.
 */
export const LocationForm: FunctionComponent<LocationFormProps> = ({
  onSubmitFn,
  address,
  editTaxCRM = false,
  tenantId,
  addressDetails,
  hqAddress,
  onCancel,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { tenant_id } = useStoreState();
  const { roleIsSellerAdmin, roleIsSomeKindOfBuyer } = useAuthContext();
  const { notifyInfo } = useNotifications();
  const { t } = useTranslation();
  const countries = useCountriesList();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [is_default_state_set, set_is_default_state_set] = useState(false);
  const [copyHqAddress, setCopyHqAddress] = useState(
    address?.same_as_hq || false
  ); // "Copy HQ address" toggle state
  const [storedCountry, setStoredCountry] = useState(
    address?.country === "USA" ? "US" : address?.country
  );

  const defaultExistingUser = useMemo(
    () => ({ tenant_id: tenantId ?? tenant_id } as User),
    [tenantId, tenant_id]
  );

  const methodsOfUseForm = useFormWrapper({
    shouldUnregister: false,
    resolver: zodResolver(
      z.object({
        company_name: zodRequiredString(t),
        ...zodAddressDefault(t),
        external_id: z.string().optional(),
        tax_id: z.string().optional().nullable(),
        crm_id: z.string().optional().nullable(),
        is_active: z.boolean(),
        same_as_hq: z.boolean().optional(),
        points_of_contact: pointsOfContactArraySchema(t),
      })
    ),
    defaultValues: address
      ? {
          address1: address.address1,
          address2: address.address2 ?? undefined,
          company_name: address.company_name ?? "",
          tax_id: address.tax_id ?? undefined,
          crm_id: address.crm_id ?? undefined,
          external_id: address.external_identity?.external_id,
          city: address.city,
          county: address?.county ?? undefined,
          country: getCountryOption(countries, address.country) ?? {
            label: "",
            value: "",
          },
          state: { label: undefined, value: address.state },
          postal_code: address.postal_code,
          is_active: address.is_active,
          same_as_hq: address.same_as_hq ?? false,
          points_of_contact: addressToPointsOfContactInputs(address),
        }
      : {
          country: { label: "", value: "" },
          state: { label: "", value: "" },
          is_active: true,
          same_as_hq: false,
          points_of_contact: [blankPointOfContact],
        },
  });
  const {
    handleSubmit,
    register,
    formState,
    control,
    errors,
    reset,
    watch,
    setValue,
  } = methodsOfUseForm;
  const { fields, append, remove } = useFieldArray<LocationFormPOCInput>({
    control,
    name: "points_of_contact",
  });
  const selectedCountry = watch("country");
  const states = useStatesList(selectedCountry?.value);
  const watchIsActive = watch("is_active", address?.is_active);
  const watchIsSameAsHQ = watch("same_as_hq", address?.same_as_hq);

  const onSubmit = async (inputs: LocationInputs) => {
    if (submitting) return;

    if (editTaxCRM) {
      const taxIdChanged = didFieldChange(inputs.tax_id, address?.tax_id);
      const crmIdChanged = didFieldChange(inputs.crm_id, address?.crm_id);

      if (!taxIdChanged && !crmIdChanged) {
        notifyInfo(t("There is nothing to update."));
        onCancel?.();
        return;
      }
    }

    setSubmitting(true);
    // Copy HQ address is a one-time convenience copy of the address fields
    // only; unlike billing addresses, it never links the POC(s) attached to
    // this shipping location to HQ, so same_as_hq is always persisted false.
    const didSucceed = await onSubmitFn({
      ...inputs,
      same_as_hq: false,
    });
    setSubmitting(false);

    if (didSucceed) {
      // Clear the form.
      reset();
    }
  };

  const cancelDeactivateAddress = () => {
    reset();
    setShowConfirmationModal(false);
  };

  const checkActiveAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const is_checked = e.currentTarget.checked;
    if (address?.is_active && addressDetails?.has_price_tiers && !is_checked) {
      setShowConfirmationModal(true);
    }
    setValue("is_active", is_checked);
  };

  const checkCopyHqAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const is_checked = e.currentTarget.checked;
    setCopyHqAddress(is_checked);
    handleCopyHqAddressToggle(is_checked, hqAddress);
    setValue("same_as_hq", is_checked);
  };

  // Handle the "Copy HQ address" toggle switch change. Unlike the billing
  // address form, this only mirrors the HQ address fields — it never
  // touches the points of contact attached to this shipping location.
  const handleCopyHqAddressToggle = useCallback(
    (shouldCopyHqAddress: boolean, hqAddress: IAddress | undefined) => {
      if (shouldCopyHqAddress && hqAddress) {
        // Prefill the form with HQ address details
        setValue("company_name", hqAddress.company_name || "");
        setValue("address1", hqAddress.address1 || "");
        setValue("address2", hqAddress.address2 || "");
        setValue("city", hqAddress.city || "");
        setValue("county", hqAddress.county || "");
        setValue("postal_code", hqAddress.postal_code || "");

        // Ensure countries are loaded before setting
        setValue(
          "country",
          getCountryOption(countries, hqAddress.country) || {
            label: "",
            value: "",
          }
        );

        // Ensure states are loaded before setting
        setValue(
          "state",
          getStateOption(states, hqAddress.state) || { label: "", value: "" }
        );

        setValue("same_as_hq", true);
      } else {
        // Clear the address fields if the toggle is off
        setValue("company_name", "");
        setValue("address1", "");
        setValue("address2", "");
        setValue("city", "");
        setValue("county", "");
        setValue("postal_code", "");
        setValue("state", { label: "", value: "" });
        setValue("country", { label: "", value: "" });
        setValue("tax_id", "");
        setValue("same_as_hq", false);
      }
    },
    [setValue, countries, states]
  );

  useEffect(() => {
    if (selectedCountry?.value && selectedCountry?.value !== storedCountry) {
      setStoredCountry(selectedCountry?.value);
      setValue("address1", "");
      setValue("address2", "");
      setValue("city", "");
      setValue("county", "");
      setValue("postal_code", "");
      setValue("state", { label: "", value: "" });
    }
  }, [selectedCountry, storedCountry, setValue]);

  useEffect(() => {
    if (
      copyHqAddress &&
      hqAddress &&
      countries.length > 0 &&
      states.length > 0
    ) {
      // Prefill the form with HQ address details once all dependencies are ready
      handleCopyHqAddressToggle(true, hqAddress);
    }
  }, [copyHqAddress, hqAddress, countries, states, handleCopyHqAddressToggle]); // Trigger when any of these change

  useEffect(() => {
    if (countries.length > 0 && address) {
      setValue("country", getCountryOption(countries, address.country));
    }
  }, [countries, address, setValue]);

  useEffect(() => {
    if (states.length > 0 && !is_default_state_set) {
      address?.state &&
        setValue("state", getStateOption(states, address.state));
      set_is_default_state_set(true);
    }
  }, [address?.state, is_default_state_set, setValue, states]);

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormSectionHeading>{t("Address")}</FormSectionHeading>
        <TextField
          name="company_name"
          label={t("Company Name")}
          theref={register({
            required: true,
          })}
          formState={formState}
          readOnly={editTaxCRM || copyHqAddress}
          errors={errors}
          type="text"
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="country"
          disabled={editTaxCRM || copyHqAddress}
          autoComplete={"country-name"}
          placeholder={t("Country")}
          options={countries}
          rules={{
            required: true,
          }}
          errors={errors}
          formState={formState}
        />
        {selectedCountry && selectedCountry.value && (
          <>
            <TextField
              name="address1"
              autoComplete="address-line1"
              label={getAddressOneInputLabel(selectedCountry.value, t)}
              readOnly={editTaxCRM || copyHqAddress}
              theref={register({
                required: true,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
            <TextField
              name="address2"
              autoComplete="address-line2"
              label={getAddressTwoInputLabel(selectedCountry.value, t)}
              readOnly={editTaxCRM || copyHqAddress}
              theref={register({
                required: false,
              })}
              formState={formState}
              errors={errors}
              type="text"
            />
            <Flex>
              <Flex2
                style={{
                  marginRight: selectedCountry.value === "IN" ? "14px" : "0",
                }}
              >
                <Controller
                  as={SelectBoxV2}
                  control={control}
                  name="state"
                  autoComplete="address-level1"
                  placeholder={getStatesInputLabel(selectedCountry.value, t)}
                  disabled={editTaxCRM || copyHqAddress}
                  id="portSelectBox"
                  options={states}
                  rules={{
                    required: true,
                  }}
                  errors={errors}
                  formState={formState}
                />
              </Flex2>
              {selectedCountry.value === "IN" && (
                <Flex1>
                  <TextField
                    name="county"
                    label={t("Taluka")}
                    readOnly={editTaxCRM || copyHqAddress}
                    theref={register({
                      required: false,
                    })}
                    formState={formState}
                    errors={errors}
                    type="text"
                  />
                </Flex1>
              )}
            </Flex>
            <Flex>
              <Flex2>
                <TextField
                  name="city"
                  label={t("City")}
                  readOnly={editTaxCRM || copyHqAddress}
                  theref={register({
                    required: true,
                  })}
                  formState={formState}
                  errors={errors}
                  type="text"
                />
              </Flex2>
              <Flex1>
                <TextField
                  name="postal_code"
                  autoComplete="postal-code"
                  label={getZipCodeInputLabel(selectedCountry.value, t)}
                  readOnly={editTaxCRM || copyHqAddress}
                  theref={register({
                    required: true,
                  })}
                  formState={formState}
                  errors={errors}
                  type="text"
                />
              </Flex1>
            </Flex>
          </>
        )}
        {!roleIsSomeKindOfBuyer && (
          <TextField
            name="crm_id"
            label={t("CRM ID")}
            readOnly={
              !roleIsSellerAdmin ||
              (editTaxCRM && address?.crm_id
                ? address?.crm_id?.length > 0
                : copyHqAddress) // Disable if copyHqAddress is true
            }
            theref={register({
              required: false,
            })}
            formState={formState}
            errors={errors}
            type="text"
          />
        )}
        <TextField
          name="tax_id"
          label={t("Tax ID")}
          readOnly={
            editTaxCRM && address?.tax_id ? address?.tax_id?.length > 0 : false
          }
          theref={register({
            required: false,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />
        {tenant_id !== tenantId && (
          <TextField
            name="external_id"
            label={t("Location ID")}
            readOnly={editTaxCRM || copyHqAddress}
            theref={register({
              required: false,
            })}
            formState={formState}
            errors={errors}
            type="text"
          />
        )}
        <ToggleRow>
          <ToggleSwitchV2
            label={watchIsActive ? t("Active") : t("Inactive")}
            name="is_active"
            ref={register({ required: true })}
            disabled={editTaxCRM || copyHqAddress}
            checked={watchIsActive}
            onChange={checkActiveAddress}
          />
          <ToggleSwitchV2
            label={t("Copy HQ address")}
            name="same_as_hq"
            ref={register}
            checked={watchIsSameAsHQ}
            onChange={checkCopyHqAddress}
            disabled={editTaxCRM}
          />
        </ToggleRow>
        <PointOfContactAccordion
          methodsOfUseForm={methodsOfUseForm}
          fields={fields}
          append={append}
          remove={remove}
          defaultExistingUser={defaultExistingUser}
          disabled={editTaxCRM}
        />
        {onCancel ? (
          <FullWidthButtonGroup>
            <TertiaryButtonLargeV2
              type="button"
              onClick={onCancel}
              testid="cancel-location-form"
            >
              {t("Cancel")}
            </TertiaryButtonLargeV2>
            <PrimaryButtonLargeV2
              type="submit"
              loading={submitting}
              testid="save-location-form"
            >
              {address ? t("Save") : t("Add")}
            </PrimaryButtonLargeV2>
          </FullWidthButtonGroup>
        ) : (
          <SubmitButtonContainer>
            <PrimaryButtonMedium loading={submitting}>
              {address ? t("Save Change") : t("Add")}
            </PrimaryButtonMedium>
          </SubmitButtonContainer>
        )}
      </Form>
      <Modal
        overlay
        show={showConfirmationModal}
        closeModal={cancelDeactivateAddress}
        modalWidth={"680px"}
      >
        <ModalWrapper>
          <WarningTitle>{t("This Action will affect pricing")}</WarningTitle>
          <div>
            {t(
              "There are currently active price offers for this location. Disabling this location will remove any existing price offers."
            )}
          </div>
          <div>{t("Would you like to proceed?")}</div>
          <ButtonContainer
            style={{ marginTop: "30px", justifyContent: "center" }}
          >
            <TertiaryButtonMediumV2 onClick={cancelDeactivateAddress}>
              {t("Cancel")}
            </TertiaryButtonMediumV2>
            <PrimaryButtonMediumV2
              onClick={() => setShowConfirmationModal(false)}
            >
              {t("Proceed")}
            </PrimaryButtonMediumV2>
          </ButtonContainer>
        </ModalWrapper>
      </Modal>
    </>
  );
};
